export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  tags: string[];
  coverImage: string;
  publishedAt: string;
  readingTime: number;
  metaTitle: string;
  metaDescription: string;
  status: 'published' | 'draft' | 'archived';
  featured: boolean;
}

export const categoryColors: Record<string, { bg: string; text: string }> = {
  'Automatisation': { bg: 'bg-amber-50', text: 'text-amber-700' },
  'Airtable': { bg: 'bg-blue-50', text: 'text-blue-700' },
  'Make': { bg: 'bg-purple-50', text: 'text-purple-700' },
  'Softr': { bg: 'bg-teal-50', text: 'text-teal-700' },
  'Logistique': { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  'Transport': { bg: 'bg-orange-50', text: 'text-orange-700' },
  'Cas clients': { bg: 'bg-rose-50', text: 'text-rose-700' },
  'Tutoriels': { bg: 'bg-indigo-50', text: 'text-indigo-700' },
  'Outils internes': { bg: 'bg-cyan-50', text: 'text-cyan-700' },
};

export const allCategories = Object.keys(categoryColors);

export const blogPosts: BlogPost[] = [
  {
    slug: 'airtable-pme-base-de-donnees',
    title: 'Airtable pour les PME : une base de données qui remplace 4 outils en un',
    excerpt: 'Comment les PME utilisent Airtable pour gérer CRM, projets, stocks et processus sans logiciel coûteux. Guide pratique avec cas concrets.',
    category: 'Airtable',
    tags: ['Airtable', 'Outils internes'],
    coverImage: '/images/blog/airtable-pme-cover.jpg',
    publishedAt: '2026-02-28',
    readingTime: 9,
    metaTitle: 'Airtable PME : la base de données qui simplifie tout',
    metaDescription: 'Comment les PME utilisent Airtable pour gérer CRM, projets, stocks et processus sans logiciel coûteux. Guide pratique avec cas concrets.',
    status: 'published',
    featured: true,
    content: `
      <p>Excel est gratuit, tout le monde le connaît, et il fait le boulot… jusqu'à un certain point. Quand les fichiers se multiplient, que plusieurs personnes modifient la même feuille en même temps, que les formules cassent sans qu'on sache pourquoi et que les données critiques de votre entreprise reposent sur un tableau que seul un collègue sait lire — c'est le signal qu'il faut passer à autre chose. Airtable est souvent la réponse que cherchent les PME : la puissance d'une base de données relationnelle, avec une interface aussi accessible qu'un tableur. Aucune compétence technique requise. Voici ce que ça change concrètement.</p>

      <h2>Airtable, c'est quoi exactement ?</h2>
      <p>Imaginez un Excel qui aurait fait de la musculation. Airtable ressemble à un tableur, mais c'est en réalité une base de données relationnelle : vous pouvez créer des liens entre vos tables, déclencher des automatisations, filtrer et agréger vos données selon des dizaines de critères, et donner accès à des vues différentes selon les profils de vos collaborateurs.</p>
      <p>Concrètement : vous avez une table "Clients", une table "Projets" et une table "Factures". Dans Airtable, ces trois tables sont reliées. Quand vous ouvrez la fiche d'un client, vous voyez directement tous ses projets en cours et ses factures associées, sans ouvrir trois fichiers différents. Tout est centralisé, à jour, accessible depuis n'importe quel appareil.</p>
      <p>Côté tarification, Airtable propose des plans à partir de 20 € par utilisateur et par mois, avec une version gratuite suffisante pour démarrer. Pour une PME de 5 à 10 personnes, le coût total reste très inférieur à la plupart des logiciels métier du marché.</p>

      <h2>3 cas concrets où Airtable transforme le quotidien d'une PME</h2>

      <h3>1. Le CRM commercial sans abonnement à 1 000 €/an</h3>
      <p>70 % des PME que nous accompagnons gèrent encore leurs prospects dans Excel ou, pire, dans leur boîte mail. Le problème n'est pas l'outil en soi, c'est l'absence de structure : pas de suivi d'étape, pas de relance planifiée, pas de visibilité sur le pipeline.</p>
      <p>Avec Airtable, vous configurez en quelques heures un CRM adapté exactement à votre cycle de vente : vos étapes, vos champs personnalisés, vos automatisations de relance. La vue Kanban vous donne une vision visuelle et immédiate de où en est chaque deal. Et contrairement à un CRM packagé, vous ne payez pas pour des fonctionnalités dont vous n'avez pas besoin.</p>

      <h3>2. Le suivi de projets et de prestataires</h3>
      <p>Chef de projet, responsable opérationnel ou dirigeant qui gère tout lui-même — peu importe le profil, la difficulté est la même : savoir en temps réel où en est chaque livrable, qui fait quoi et ce qui est en retard. Airtable permet de construire un tableau de bord de suivi de projets en quelques heures, connecté à vos outils existants (Slack, Google Calendar, email) via des automatisations Make ou les automatisations natives d'Airtable.</p>
      <p>Le vrai avantage par rapport à un outil de gestion de projet classique : vous adaptez la structure à vos process, pas l'inverse.</p>

      <h3>3. La gestion de catalogue produit ou de stock</h3>
      <p>Pour les PME qui ont des produits physiques ou des ressources à suivre (matières premières, équipements, licences logicielles…), Airtable sert de couche de données centrale. Vous renseignez une fois, toutes les vues se mettent à jour : la vue comptabilité voit les coûts, le commercial voit les disponibilités, la logistique voit les mouvements de stock. Fini les fichiers Excel en double et les erreurs de synchronisation.</p>

      <h2>Airtable vs Excel : la différence qui change tout</h2>
      <p>Excel est un outil de calcul. Airtable est un outil de données. Ce n'est pas la même chose.</p>
      <p>Dans Excel, une ligne est une ligne. Dans Airtable, une ligne est un <strong>enregistrement</strong> qui peut être relié à d'autres enregistrements, enrichi de pièces jointes, commenté, suivi dans le temps et exposé dans des interfaces différentes selon les utilisateurs. Quand vous avez besoin de collaborer à plusieurs sur des données structurées, la différence devient immédiatement évidente.</p>
      <p>Ce que nos clients décrivent systématiquement après la migration : ils passent bien moins de temps à chercher des informations, à corriger des erreurs de saisie ou à synchroniser des fichiers en double. Ce n'est pas anodin pour une PME où chaque heure compte.</p>

      <blockquote>
        <p>« Airtable n'est pas une fin en soi — c'est une fondation. La vraie valeur, c'est ce qu'on construit dessus : les automatisations, les interfaces métier, les connexions avec vos autres outils. Une base bien structurée dès le départ vous évite de tout refaire dans 18 mois. » — L'équipe NoCodeo</p>
      </blockquote>

      <h2>Comment mettre en place Airtable sans se perdre</h2>
      <p>Le piège avec Airtable, c'est de vouloir tout mettre dedans sans avoir réfléchi à la structure au préalable. Une base mal construite devient vite impossible à maintenir — exactement comme un Excel qu'une seule personne comprend.</p>
      <p>Voici l'approche que nous recommandons pour une première mise en place :</p>
      <ul>
        <li><strong>Étape 1 — Choisir un seul cas d'usage</strong> pour commencer. Le CRM est souvent le plus impactant. Inutile de vouloir tout migrer d'un coup.</li>
        <li><strong>Étape 2 — Définir vos entités principales</strong> : qu'est-ce que vous gérez ? Des clients, des projets, des produits, des contrats ? Chaque entité devient une table.</li>
        <li><strong>Étape 3 — Identifier les relations</strong> entre ces entités : un client a plusieurs projets, un projet a plusieurs tâches, etc. C'est là que la puissance relationnelle d'Airtable prend tout son sens.</li>
        <li><strong>Étape 4 — Ne pas tout automatiser immédiatement</strong>. Stabilisez d'abord votre structure de données, puis ajoutez progressivement les automatisations.</li>
        <li><strong>Étape 5 — Former votre équipe</strong> sur les bases de l'outil. Airtable est intuitif, mais une formation de 2 à 3 heures évite les mauvaises habitudes qui fragilisent la base avec le temps.</li>
      </ul>

      <h2>Conclusion : commencez par un cas d'usage, pas par une migration complète</h2>
      <p>Airtable est un outil puissant, mais sa valeur dépend de la qualité de la structure qu'on y construit. La bonne nouvelle : on n'a pas besoin d'être parfait pour démarrer. Un CRM simple, bien construit, apporte déjà un gain immédiat et mesurable.</p>
      <p>Si vous voulez savoir si Airtable est adapté à votre situation spécifique — votre secteur, vos processus, vos outils existants — <a href="https://no-codeo.com">prenez contact avec l'équipe NoCodeo</a>. On analyse votre contexte et on vous dit clairement ce qui a du sens (et ce qui n'en a pas).</p>

      <hr />
      <p><em>Sources : retours clients NoCodeo ; <a href="https://wesmart-nocode.fr/10-cas-dusage-dairtable-pour-les-pme/" target="_blank" rel="noopener">WeSmart NoCode — 10 cas d'usage Airtable pour les PME</a> ; <a href="https://www.impli.fr/avis/airtable" target="_blank" rel="noopener">Impli.fr — Avis Airtable 2026</a></em></p>
    `
  },
  {
    slug: 'automatiser-gestion-tournees-airtable-make',
    title: 'Comment automatiser la gestion de vos tournées avec Airtable et Make',
    excerpt: 'Découvrez comment connecter Airtable et Make pour planifier, suivre et optimiser vos tournées de livraison sans une ligne de code.',
    category: 'Automatisation',
    tags: ['Automatisation', 'Logistique', 'Airtable', 'Make'],
    coverImage: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=450&fit=crop',
    publishedAt: '2026-01-15',
    readingTime: 7,
    metaTitle: 'Automatiser vos tournées avec Airtable et Make | NoCodeo',
    metaDescription: 'Guide complet pour automatiser la gestion de vos tournées de livraison avec Airtable et Make. Planification, suivi, optimisation sans code.',
    status: 'published',
    featured: false,
    content: `
      <h2>Pourquoi automatiser la gestion de vos tournées ?</h2>
      <p>La gestion manuelle des tournées de livraison est l'un des plus grands gouffres de productivité dans le secteur logistique. Entre la planification, le suivi en temps réel et les ajustements de dernière minute, vos équipes passent des heures chaque semaine sur des tâches répétitives.</p>
      <p>Avec une combinaison intelligente d'Airtable (pour centraliser vos données) et de Make (pour automatiser les flux), vous pouvez réduire ce temps de <strong>60 à 80%</strong>.</p>

      <h2>Étape 1 : Structurer vos données dans Airtable</h2>
      <p>La première étape consiste à créer une base Airtable solide avec les tables suivantes :</p>
      <ul>
        <li><strong>Clients</strong> : nom, adresse, coordonnées GPS, créneau préféré</li>
        <li><strong>Tournées</strong> : date, conducteur assigné, statut, nombre d'arrêts</li>
        <li><strong>Points de livraison</strong> : lien vers client, ordre de passage, statut</li>
        <li><strong>Conducteurs</strong> : nom, véhicule, zone géographique</li>
      </ul>

      <h3>Bonnes pratiques de structuration</h3>
      <p>Utilisez les champs de type "Link to another record" pour créer des relations entre vos tables. Cela vous permettra de générer des vues filtrées par conducteur, par zone ou par statut.</p>

      <blockquote>
        <p>« La qualité de votre automatisation dépend directement de la qualité de votre structuration de données. » — Équipe NoCodeo</p>
      </blockquote>

      <h2>Étape 2 : Créer les scénarios Make</h2>
      <p>Une fois vos données structurées, vous pouvez créer des scénarios Make pour automatiser les processus clés :</p>
      <ul>
        <li>Création automatique des tournées à partir des commandes du jour</li>
        <li>Notification SMS aux clients avec le créneau de livraison</li>
        <li>Mise à jour du statut en temps réel</li>
        <li>Rapport de fin de journée envoyé par email</li>
      </ul>

      <h3>Exemple de scénario : notification client</h3>
      <p>Le scénario se déclenche chaque matin à 7h. Il récupère les livraisons du jour, génère un message personnalisé et l'envoie par SMS via Twilio ou par email.</p>

      <h2>Étape 3 : Suivre les KPIs en temps réel</h2>
      <p>Grâce aux vues et dashboards Airtable, vous pouvez suivre en temps réel :</p>
      <ul>
        <li>Le taux de livraison réussie</li>
        <li>Le temps moyen par tournée</li>
        <li>Les incidents et retards</li>
        <li>La charge par conducteur</li>
      </ul>
      <p>Ces indicateurs vous permettent d'identifier rapidement les axes d'amélioration et de prendre des décisions basées sur des données concrètes.</p>

      <h2>Résultats attendus</h2>
      <p>Nos clients qui ont mis en place ce type de système constatent en moyenne :</p>
      <ul>
        <li>70% de temps gagné sur la planification</li>
        <li>30% de réduction des erreurs de livraison</li>
        <li>Une satisfaction client en hausse grâce aux notifications proactives</li>
      </ul>
    `
  },
  {
    slug: 'airtable-vs-excel-operations',
    title: 'Airtable vs Excel : lequel choisir pour piloter vos opérations ?',
    excerpt: 'Comparatif objectif entre Airtable et Excel pour la gestion opérationnelle. Critères, cas d\'usage et recommandations.',
    category: 'Airtable',
    tags: ['Airtable', 'Logistique'],
    coverImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=450&fit=crop',
    publishedAt: '2025-12-20',
    readingTime: 5,
    metaTitle: 'Airtable vs Excel pour vos opérations | Comparatif NoCodeo',
    metaDescription: 'Airtable ou Excel pour piloter vos opérations logistiques ? Comparatif objectif avec critères, cas d\'usage et recommandations pratiques.',
    status: 'published',
    featured: false,
    content: `
      <h2>Pourquoi ce comparatif est important</h2>
      <p>Excel est l'outil le plus utilisé au monde pour gérer des données. Mais quand vos opérations grandissent, ses limites apparaissent vite : fichiers corrompus, versions multiples, pas de collaboration en temps réel.</p>
      <p>Airtable propose une approche différente : une vraie base de données avec une interface aussi simple qu'un tableur.</p>

      <h2>Critère 1 : Collaboration</h2>
      <h3>Excel</h3>
      <p>La collaboration est possible via SharePoint ou OneDrive, mais reste limitée. Les conflits de version sont fréquents et la co-édition peut être instable.</p>
      <h3>Airtable</h3>
      <p>La collaboration est native et en temps réel. Chaque utilisateur a sa propre vue, et les modifications sont synchronisées instantanément.</p>

      <h2>Critère 2 : Automatisation</h2>
      <p>Excel propose des macros VBA, puissantes mais complexes à maintenir. Airtable propose des automatisations intégrées (sans code) et une intégration native avec Make et Zapier.</p>

      <h2>Critère 3 : Structuration des données</h2>
      <p>C'est le point fort d'Airtable. Les relations entre tables, les champs typés et les vues filtrées permettent de structurer vos données de manière professionnelle.</p>

      <blockquote>
        <p>« Excel est parfait pour analyser des données. Airtable est parfait pour les gérer au quotidien. »</p>
      </blockquote>

      <h2>Notre recommandation</h2>
      <p>Si vous gérez des opérations avec plus de 3 personnes impliquées et des données qui évoluent quotidiennement, Airtable est le choix le plus adapté. Pour de l'analyse ponctuelle ou des calculs complexes, Excel reste pertinent.</p>
      <p>La bonne nouvelle : les deux outils se connectent parfaitement grâce à Make.</p>
    `
  },
  {
    slug: 'softr-application-web-equipe-terrain',
    title: 'Softr : créer une application web sans coder pour votre équipe terrain',
    excerpt: 'Guide pratique pour construire une app Softr connectée à Airtable, destinée à vos équipes terrain en logistique.',
    category: 'Softr',
    tags: ['Softr', 'Tutoriels', 'Logistique'],
    coverImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=450&fit=crop',
    publishedAt: '2025-12-10',
    readingTime: 8,
    metaTitle: 'Créer une app Softr pour vos équipes terrain | Tutoriel NoCodeo',
    metaDescription: 'Tutoriel complet pour créer une application web Softr connectée à Airtable pour vos équipes terrain. Interface mobile, formulaires, dashboards.',
    status: 'published',
    featured: false,
    content: `
      <h2>Pourquoi Softr pour les équipes terrain ?</h2>
      <p>Vos équipes terrain ont besoin d'outils simples, rapides et accessibles sur mobile. Softr permet de créer des applications web complètes connectées à Airtable, sans écrire une seule ligne de code.</p>
      <p>En quelques heures, vous pouvez déployer :</p>
      <ul>
        <li>Un portail de suivi des interventions</li>
        <li>Des formulaires de saisie terrain (bons de livraison, rapports d'incident)</li>
        <li>Un dashboard temps réel pour les managers</li>
        <li>Un espace client avec suivi des commandes</li>
      </ul>

      <h2>Étape 1 : Préparer votre base Airtable</h2>
      <p>Avant de créer votre app Softr, assurez-vous que votre base Airtable est correctement structurée avec des vues dédiées à chaque usage.</p>

      <h3>Tables recommandées</h3>
      <ul>
        <li><strong>Interventions</strong> : date, client, statut, technicien assigné</li>
        <li><strong>Techniciens</strong> : nom, email, zone, véhicule</li>
        <li><strong>Rapports</strong> : intervention liée, photos, commentaires, signature</li>
      </ul>

      <h2>Étape 2 : Configurer Softr</h2>
      <p>Connectez votre base Airtable à Softr et commencez à construire vos pages :</p>
      <ul>
        <li><strong>Page liste</strong> : toutes les interventions du jour avec filtres</li>
        <li><strong>Page détail</strong> : informations complètes + formulaire de rapport</li>
        <li><strong>Dashboard</strong> : KPIs et graphiques pour les managers</li>
      </ul>

      <h2>Étape 3 : Gérer les accès</h2>
      <p>Softr permet de gérer finement les accès par rôle (technicien, manager, client). Chaque utilisateur ne voit que les données qui le concernent.</p>

      <blockquote>
        <p>« En 2 jours, nous avons déployé une app mobile que nos 30 techniciens utilisent quotidiennement. » — Client NoCodeo</p>
      </blockquote>

      <h2>Bonnes pratiques</h2>
      <ul>
        <li>Testez sur mobile dès le début (Softr est responsive par défaut)</li>
        <li>Limitez le nombre de champs par formulaire pour faciliter la saisie terrain</li>
        <li>Utilisez les notifications Airtable + Make pour alerter en cas d'incident</li>
      </ul>
    `
  },
  {
    slug: 'make-guide-debutant-automatisations',
    title: 'Make (ex-Integromat) : le guide débutant pour vos premières automatisations',
    excerpt: 'Apprenez à créer vos premiers scénarios Make étape par étape. De la connexion d\'apps à l\'automatisation de vos processus.',
    category: 'Make',
    tags: ['Make', 'Tutoriels', 'Automatisation'],
    coverImage: 'https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?w=800&h=450&fit=crop',
    publishedAt: '2025-11-28',
    readingTime: 10,
    metaTitle: 'Guide débutant Make (Integromat) | Tutoriel NoCodeo',
    metaDescription: 'Guide complet pour débuter avec Make (ex-Integromat). Créez vos premiers scénarios d\'automatisation pas à pas.',
    status: 'published',
    featured: false,
    content: `
      <h2>Qu'est-ce que Make ?</h2>
      <p>Make (anciennement Integromat) est une plateforme d'automatisation no-code qui permet de connecter des centaines d'applications entre elles. Vous créez des "scénarios" visuels qui s'exécutent automatiquement selon vos règles.</p>
      <p>Contrairement à Zapier (son concurrent principal), Make offre une interface visuelle plus puissante et des tarifs plus compétitifs pour les usages avancés.</p>

      <h2>Les concepts de base</h2>
      <h3>Les modules</h3>
      <p>Un module est une action dans un scénario : lire des données, envoyer un email, créer un enregistrement, etc. Chaque application connectée propose ses propres modules.</p>

      <h3>Les scénarios</h3>
      <p>Un scénario est une séquence de modules connectés entre eux. Par exemple : "Quand un formulaire est soumis → créer un enregistrement Airtable → envoyer un email de confirmation".</p>

      <h3>Les déclencheurs</h3>
      <p>Chaque scénario commence par un déclencheur : un événement qui lance l'exécution. Il peut être temporel (toutes les heures) ou événementiel (nouveau formulaire reçu).</p>

      <h2>Votre premier scénario en 5 étapes</h2>
      <ul>
        <li><strong>Étape 1</strong> : Créez un compte Make gratuit</li>
        <li><strong>Étape 2</strong> : Choisissez un déclencheur (ex : Google Sheets - nouvelle ligne)</li>
        <li><strong>Étape 3</strong> : Ajoutez un module d'action (ex : Gmail - envoyer un email)</li>
        <li><strong>Étape 4</strong> : Mappez les données (ex : email du client dans le champ destinataire)</li>
        <li><strong>Étape 5</strong> : Testez et activez votre scénario</li>
      </ul>

      <blockquote>
        <p>« Make m'a permis d'automatiser en 1h ce que je faisais manuellement en 2h chaque jour. » — Participant formation NoCodeo</p>
      </blockquote>

      <h2>3 scénarios utiles pour débuter</h2>
      <h3>1. Notification Slack à chaque nouvelle commande</h3>
      <p>Connectez votre CRM ou votre base Airtable à Slack pour être notifié instantanément de chaque nouvelle commande.</p>

      <h3>2. Synchronisation Airtable ↔ Google Sheets</h3>
      <p>Gardez vos données synchronisées entre Airtable (opérationnel) et Google Sheets (reporting) automatiquement.</p>

      <h3>3. Envoi automatique de rapports hebdomadaires</h3>
      <p>Chaque lundi matin, Make génère un rapport à partir de vos données Airtable et l'envoie par email à votre équipe.</p>

      <h2>Aller plus loin</h2>
      <p>Make propose des fonctionnalités avancées comme les routeurs (pour créer des branches conditionnelles), les itérateurs (pour traiter des listes) et les agrégateurs. Notre formation Make couvre tous ces sujets en détail.</p>
    `
  },
  {
    slug: '5-outils-nocode-pme-transport-2025',
    title: '5 outils no-code indispensables pour les PME du transport en 2025',
    excerpt: 'Sélection des meilleurs outils no-code pour digitaliser une PME du transport. Comparatif, prix et cas d\'usage concrets.',
    category: 'Transport',
    tags: ['Transport', 'Automatisation'],
    coverImage: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5eb19?w=800&h=450&fit=crop',
    publishedAt: '2025-11-15',
    readingTime: 6,
    metaTitle: '5 outils no-code pour PME transport 2025 | NoCodeo',
    metaDescription: 'Les 5 outils no-code essentiels pour digitaliser une PME du transport en 2025. Airtable, Make, Softr et plus encore.',
    status: 'published',
    featured: false,
    content: `
      <h2>Le no-code, un levier pour les PME du transport</h2>
      <p>Les PME du transport font face à une pression croissante : exigences clients, réglementation, concurrence des grands groupes. Le no-code leur permet de se digitaliser rapidement et à moindre coût, sans dépendre d'un service informatique.</p>

      <h2>1. Airtable — Votre base de données opérationnelle</h2>
      <p>Airtable remplace Excel pour la gestion quotidienne de vos opérations. Clients, tournées, conducteurs, véhicules : tout est centralisé dans une interface intuitive.</p>
      <ul>
        <li><strong>Prix</strong> : Gratuit jusqu'à 1 000 lignes, puis à partir de 20$/mois</li>
        <li><strong>Cas d'usage</strong> : Planning conducteurs, suivi flotte, base clients</li>
      </ul>

      <h2>2. Make — L'automatisation de vos processus</h2>
      <p>Make connecte vos outils entre eux et automatise les tâches répétitives. Plus de saisie en double, plus d'oublis.</p>
      <ul>
        <li><strong>Prix</strong> : Gratuit pour 1 000 opérations/mois, puis à partir de 9$/mois</li>
        <li><strong>Cas d'usage</strong> : Notifications, rapports automatiques, synchronisation</li>
      </ul>

      <h2>3. Softr — Des applications pour vos équipes</h2>
      <p>Softr transforme vos données Airtable en applications web utilisables par vos équipes terrain, vos clients ou vos partenaires.</p>
      <ul>
        <li><strong>Prix</strong> : Gratuit pour les fonctionnalités de base, puis à partir de 49$/mois</li>
        <li><strong>Cas d'usage</strong> : Portail conducteur, espace client, dashboard manager</li>
      </ul>

      <h2>4. Notion — La documentation d'équipe</h2>
      <p>Notion centralise vos procédures, guides d'utilisation et documentation interne. Idéal pour l'onboarding des nouveaux conducteurs.</p>
      <ul>
        <li><strong>Prix</strong> : Gratuit pour un usage personnel, puis à partir de 8$/mois</li>
        <li><strong>Cas d'usage</strong> : Procédures, formation interne, base de connaissances</li>
      </ul>

      <h2>5. Tally — Des formulaires professionnels</h2>
      <p>Tally crée des formulaires élégants et connectables à Airtable ou Make. Parfait pour les bons de livraison digitaux ou les enquêtes de satisfaction.</p>
      <ul>
        <li><strong>Prix</strong> : Gratuit pour un usage illimité de base</li>
        <li><strong>Cas d'usage</strong> : Bons de livraison, réclamations, questionnaires</li>
      </ul>

      <blockquote>
        <p>« Avec ces 5 outils, une PME de transport peut digitaliser 80% de ses processus pour moins de 200€/mois. »</p>
      </blockquote>

      <h2>Comment démarrer ?</h2>
      <p>Commencez par Airtable pour structurer vos données. Ajoutez ensuite Make pour automatiser les flux les plus chronophages. Softr viendra en complément pour créer des interfaces utilisateur.</p>
      <p>Besoin d'accompagnement ? NoCodeo vous guide de A à Z dans cette transformation.</p>
    `
  },
  {
    slug: 'creer-outil-interne-sans-developpeur',
    title: 'Créer un outil interne sur mesure sans développeur : c\'est possible (et moins cher que vous ne le pensez)',
    excerpt: 'Découvrez comment créer un outil interne sur mesure pour votre PME sans coder ni recruter un développeur. Stack no-code, coûts et exemples concrets.',
    category: 'Outils internes',
    tags: ['Outils internes', 'Airtable', 'Softr', 'Make', 'Automatisation'],
    coverImage: '/images/blog/outil-interne-cover.png',
    publishedAt: '2026-02-21',
    readingTime: 8,
    metaTitle: 'Outil interne PME sans développeur : comment faire',
    metaDescription: 'Découvrez comment créer un outil interne sur mesure pour votre PME sans coder ni recruter un développeur. Stack no-code, coûts et exemples concrets.',
    status: 'published',
    featured: false,
    content: `
      <p>Vous avez un process spécifique à votre métier. Vous avez cherché un logiciel qui correspond exactement à vos besoins. Vous n'en avez pas trouvé — ou alors vous en avez trouvé un, mais il est trop complexe, trop cher, ou trop rigide. La solution évidente serait de faire développer un outil sur mesure, mais le budget d'un développement classique dépasse largement ce que vous pouvez engager. Alors vous continuez avec Excel et des bricolages qui tiennent debout... jusqu'à ce que ça casse. Il existe une troisième voie : construire votre outil interne avec des technologies no-code. Robuste, maintenable, et livrable en quelques semaines.</p>

      <h2>Pourquoi les logiciels du marché ne correspondent jamais exactement à vos besoins</h2>
      <p>Les éditeurs de logiciels construisent des outils pour des milliers d'entreprises différentes. Pour que leur produit soit rentable, il doit répondre aux besoins du plus grand nombre — pas aux vôtres en particulier. C'est pourquoi vous finissez toujours avec des fonctionnalités en trop que vous n'utiliserez jamais, et des fonctionnalités manquantes qui vous obligent à maintenir des fichiers parallèles.</p>
      <p>Le problème s'aggrave avec le temps : votre process évolue, mais le logiciel, lui, ne bouge pas (ou change dans une direction qui ne vous convient pas). Résultat : votre outil interne devient un boulet plutôt qu'un levier.</p>
      <p>La vraie question n'est pas "quel logiciel correspond à mes besoins ?" mais "comment construire exactement ce dont j'ai besoin, sans dépendre d'une équipe de développeurs ?"</p>

      <h2>Le stack no-code pour créer votre outil interne</h2>
      <p>Il n'existe pas une seule façon de construire un outil interne no-code. Mais dans la grande majorité des cas pour les PME, trois briques suffisent à couvrir l'essentiel.</p>

      <h3>Airtable — la couche de données</h3>
      <p>Airtable est votre base de données. C'est là que vivent toutes vos informations structurées : clients, commandes, projets, ressources, contrats, équipements... Tout ce que vous gérez aujourd'hui dans des fichiers Excel éparpillés peut être centralisé dans une base Airtable bien construite.</p>
      <p>La force d'Airtable, c'est sa flexibilité : vous modélisez vos données exactement comme votre métier fonctionne, pas comme un éditeur de logiciel a décidé que vous deviez fonctionner.</p>

      <h3>Softr — l'interface utilisateur</h3>
      <p>Airtable est puissant, mais son interface n'est pas toujours adaptée à tous les profils d'utilisateurs. Softr permet de construire des interfaces web propres, ergonomiques et accessibles depuis n'importe quel navigateur — sans écrire une ligne de code.</p>
      <p>Concrètement, avec Softr vous pouvez créer un portail où chaque collaborateur ne voit que les données qui le concernent, où un client peut consulter l'avancement de sa commande, ou où un responsable terrain saisit ses rapports d'intervention depuis son téléphone. Softr lit vos données Airtable et les présente dans une interface que vous avez conçue pour vos utilisateurs.</p>

      <h3>Make — les automatisations</h3>
      <p>Make (anciennement Integromat) est le moteur qui fait circuler les informations entre vos outils et déclenche les actions automatiques. Quand un collaborateur met à jour un statut dans l'interface Softr, Make peut déclencher un email, mettre à jour une autre table Airtable, créer une tâche dans votre outil de gestion de projet, ou envoyer une notification sur Slack.</p>
      <p>C'est Make qui transforme votre outil interne statique en un système vivant, qui réagit et s'adapte à ce qui se passe dans votre entreprise en temps réel.</p>

      <h2>Deux exemples concrets d'outils internes construits pour des PME</h2>

      <h3>Cas 1 — Un bureau d'études de 12 personnes</h3>
      <p>Le client gérait ses affaires dans Excel : devis, suivi des interventions, facturation, planning des techniciens. Chaque mise à jour demandait des modifications manuelles dans trois fichiers différents. Les erreurs de saisie étaient fréquentes, et le dirigeant n'avait jamais de vision claire sur le taux d'occupation réel de ses équipes.</p>
      <p>En huit semaines, nous avons livré un outil interne complet : base Airtable pour les affaires et les interventions, interface Softr pour la saisie terrain par les techniciens, automatisations Make pour la génération des bons d'intervention et les alertes de planning. Le dirigeant dispose aujourd'hui d'un tableau de bord en temps réel, et ses techniciens saisissent leurs rapports depuis leur téléphone en 3 minutes.</p>

      <h3>Cas 2 — Une entreprise de formation de 8 personnes</h3>
      <p>Les sessions de formation, les participants, les formateurs et les documents pédagogiques étaient gérés dans des outils disparates. La coordination prenait un temps disproportionné, et les erreurs d'inscription ou d'affectation de salle étaient récurrentes.</p>
      <p>L'outil livré centralise tout dans Airtable, expose une interface claire pour les coordinateurs via Softr, et automatise les confirmations d'inscription, les rappels de session et les envois de documents via Make. La charge administrative a été réduite de plus de moitié selon nos retours clients.</p>

      <h2>Autonomie après livraison : la vraie question à poser à votre prestataire</h2>
      <p>Un outil interne qui fonctionne bien mais que personne dans l'entreprise ne comprend ni ne peut maintenir, c'est un outil interne qui deviendra un problème dans 18 mois. Quand le prestataire ne sera plus là, quand un process changera, quand vous aurez besoin d'ajouter un champ ou de modifier une automatisation — qui le fera ?</p>
      <p>Avant de confier la construction de votre outil interne à un prestataire no-code, posez une question simple : "Est-ce que mes équipes pourront modifier et faire évoluer cet outil sans vous ?" Si la réponse est vague, fuyez.</p>
      <p>Chez NoCodeo, la formation et le transfert de compétences font partie de chaque livraison. Ce n'est pas un bonus optionnel : c'est une condition de réussite du projet.</p>

      <blockquote>
        <p>« On considère qu'un projet est réussi quand le client n'a plus besoin de nous pour les évolutions courantes. Notre objectif n'est pas de créer une dépendance — c'est de vous rendre autonome sur un système que vous comprenez et maîtrisez. » — L'équipe NoCodeo</p>
      </blockquote>

      <h2>Conclusion : votre prochain outil interne n'a pas besoin d'un budget de développement classique</h2>
      <p>Construire un outil interne sur mesure n'est plus réservé aux entreprises qui peuvent payer 50 000 € de développement. Les technologies no-code permettent aujourd'hui de livrer des systèmes robustes, évolutifs et maintenables par des non-développeurs, pour un investissement radicalement inférieur.</p>
      <p>La condition pour que ça marche : bien définir votre besoin avant de construire, choisir un prestataire qui vous rend autonome, et résister à la tentation de tout automatiser d'un coup.</p>
      <p>Si vous avez un process en tête et que vous vous demandez si le no-code peut y répondre, <a href="/conseil">l'équipe NoCodeo est disponible pour un échange sans engagement</a>. On vous dit en 30 minutes ce qu'on peut construire, ce que ça coûte et ce que vous pouvez en attendre.</p>

      <hr />
      <p><em>Sources : retours clients NoCodeo ; <a href="https://www.francenum.gouv.fr/guides-et-conseils/pilotage-de-lentreprise/numerisation-des-processus/pourquoi-utiliser-des-outils" target="_blank" rel="noopener">France Num — Outils no-code pour TPE/PME</a> ; <a href="https://happyraise.fr/workflows-no-code-pme/" target="_blank" rel="noopener">Happy Raise — Workflows no-code PME</a></em></p>
    `
  }
];

export const getPublishedPosts = () => blogPosts.filter(p => p.status === 'published').sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());

export const getFeaturedPost = () => getPublishedPosts()[0];

export const getPostBySlug = (slug: string) => blogPosts.find(p => p.slug === slug);

export const getRelatedPosts = (currentSlug: string, category: string, limit = 3) =>
  blogPosts
    .filter(p => p.slug !== currentSlug && p.status === 'published' && (p.category === category || p.tags.includes(category)))
    .slice(0, limit);

export const getPostsByCategory = (category: string) =>
  category === 'Tous'
    ? getPublishedPosts()
    : blogPosts.filter(p => p.status === 'published' && (p.category === category || p.tags.includes(category)));

export const getCategoryCounts = () => {
  const counts: Record<string, number> = {};
  const published = getPublishedPosts();
  allCategories.forEach(cat => {
    counts[cat] = published.filter(p => p.category === cat || p.tags.includes(cat)).length;
  });
  return counts;
};
